import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useMemo } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/lib/AuthContext";
import { csvCell, downloadFile, logActivity } from "@/utils";
import { Truck, Package, Calendar, ExternalLink, Trash2, Search, RefreshCcw, ChevronRight, Box, DollarSign, AlertTriangle, Printer, Download, ClipboardList } from "lucide-react";
import PDFViewerModal from "@/components/PDFViewerModal";
import ActivityLogsModal from "@/components/ActivityLogsModal";
import ReShipModal from "@/components/ReShipModal";

const getTrackingUrl = (carrier, trackingNumber) => {
  const carriers = {
    usps: `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`,
    ups: `https://www.ups.com/track?tracknum=${trackingNumber}`,
    fedex: `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`,
  };
  return carriers[carrier?.toLowerCase()] || `https://www.google.com/search?q=${trackingNumber}`;
};


export default function ShipmentsPage() {
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteOrderConfirm, setDeleteOrderConfirm] = useState(null);
  const [refundConfirm, setRefundConfirm] = useState(null);
  const [refunding, setRefunding] = useState(null);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [deletingShipment, setDeletingShipment] = useState(null);
  const [printingOrderLabels, setPrintingOrderLabels] = useState(null);
  const [undeliverConfirm, setUndeliverConfirm] = useState(null);
  const [undelivering, setUndelivering] = useState(null);
  const [previewPDF, setPreviewPDF] = useState(null);
  const [showActivityLogs, setShowActivityLogs] = useState(false);
  const [reshipShipmentId, setReshipShipmentId] = useState(null);
  const [shipmentLimit, setShipmentLimit] = useState(500);

  useEffect(() => {
    fetchShipments();
  }, [shipmentLimit]);

  useEffect(() => {
    if (location.state?.selectedOrderNumber && orders.length > 0 && !selectedOrder) {
      const order = orders.find(o => o.order_number === location.state.selectedOrderNumber);
      if (order) {
        setSelectedOrder(order);
      }
    }
  }, [location.state, orders]);

  const fetchShipments = async () => {
    try {
      const data = await api.entities.Shipment.list('-created_at', shipmentLimit);
      setShipments(data);

      const orderMap = {};
      data.forEach(shipment => {
        // Group by mailing_id instead of order_number to keep separate mailings apart
        const mailingId = shipment.mailing_id || shipment.order_number || 'Unknown';
        if (!orderMap[mailingId]) {
          orderMap[mailingId] = {
            mailing_id: mailingId,
            order_number: shipment.order_number,
            mailing_name: shipment.mailing_name,
            created_at: shipment.created_at,
            shipments: [],
            total_boxes: 0,
            total_cost: 0,
            has_refunds: false
          };
        }
        orderMap[mailingId].shipments.push(shipment);
        orderMap[mailingId].total_boxes++;
        orderMap[mailingId].total_cost += parseFloat(shipment.total_cost || 0);
        if (shipment.refund_id) {
          orderMap[mailingId].has_refunds = true;
        }
      });

      // Filter out empty orders (orders with no shipments)
      const ordersArray = Object.values(orderMap)
        .filter(order => order.shipments.length > 0)
        .sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );

      setOrders(ordersArray);
    } catch (err) {
      console.error(err);
      setError("Failed to load shipments");
    } finally {
      setLoading(false);
    }
  };

  /** Set when a refund failed and an administrator has to decide. */
  const [forceDelete, setForceDelete] = useState(null);

  const handleDelete = async (shipmentId, forceRemove = false) => {
    setDeletingShipment(shipmentId);
    setError("");

    try {
      const shipment = shipments.find(s => s.id === shipmentId);

      if (!shipment) {
        setError("Shipment not found");
        setDeletingShipment(null);
        return;
      }

      const mailingId = shipment.mailing_id;

      // Admin only. The refund is not optional - the server always attempts it
      // first, and refuses to remove a label it could not refund unless told to
      // anyway. Once the money is back the row is deleted outright.
      const result = await api.shipping.remove(shipmentId, { force: forceRemove });

      if (result.soft) {
        // The only case a row survives: Shippo took the refund but has not
        // finished it, so the transaction id is still the handle on the money.
        setError(
          `Shipment hidden. Shippo is still processing the refund, so the record ` +
          `is kept until it settles — remove it again afterwards to clear it.`
        );
      } else if (result.purged) {
        setError(
          `Shipment removed for good. The label was never refunded and is still ` +
          `live, so its Shippo transaction and cost are recorded in the activity ` +
          `log if you want to chase it later.`
        );
      }

      await fetchShipments();

      if (selectedOrder?.mailing_id === mailingId) {
        const remaining = shipments.filter(
          (s) => s.mailing_id === mailingId && s.id !== shipmentId
        );
        setSelectedOrder(remaining.length === 0 ? null : { ...selectedOrder, shipments: remaining });
      }

      setDeleteConfirm(null);
    } catch (err) {
      console.error('Delete error:', err);

      // The server keeps a shipment whose label it could not refund, because
      // the row is the only place the transaction id lives. Offer the override
      // rather than leaving the button looking broken.
      if (err.details?.can_force) {
        setForceDelete({ id: shipmentId, reason: err.message });
        setDeleteConfirm(null);
        return;
      }

      setError(
        err.isForbidden
          ? 'Removing a shipment is restricted to administrators.'
          : err.message || 'Failed to remove the shipment.'
      );
      setTimeout(() => setError(""), 8000);
    } finally {
      setDeletingShipment(null);
    }
  };

  /**
   * Open one purchased label.
   *
   * The label lives in a private bucket, so `label_path` is a storage key and
   * not something a browser can follow. This used to be an anchor pointing
   * straight at `shipment.label_url` - Base44's public URL, a field that does
   * not exist here - so the link went nowhere on every shipment. The server
   * signs the path on request; the same viewer as Print All shows it.
   */
  const handleViewLabel = async (shipment) => {
    setError("");
    try {
      const { url } = await api.shipping.labelUrl(shipment.id);
      setPreviewPDF({
        url,
        title: `Label — ${shipment.shipment_name}`,
        type: 'shipping_label',
      });
    } catch (err) {
      console.error('Label error:', err);
      setError(err.message || 'Could not open that label.');
      setTimeout(() => setError(""), 6000);
    }
  };

  const handleRefund = async (shipmentId) => {
    setRefunding(shipmentId);
    setError("");

    try {
      const shipment = shipments.find(s => s.id === shipmentId);
      const mailingId = shipment?.mailing_id;

      const result = await api.shipping.refund(shipmentId);

      // Shippo may answer QUEUED or PENDING rather than SUCCESS. Report what it
      // actually said instead of assuming the money is back.
      const status = result.shipment?.refund_status;
      if (status && status !== 'SUCCESS') {
        setError(
          `Refund submitted and is ${status.toLowerCase()}. It is not complete until ` +
          `Shippo reports SUCCESS — check back shortly.`
        );
      }

      await fetchShipments();

      if (selectedOrder && mailingId && selectedOrder.mailing_id === mailingId) {
        const refreshed = orders.find(o => o.mailing_id === mailingId);
        if (refreshed) setSelectedOrder(refreshed);
      }

      setRefundConfirm(null);
    } catch (err) {
      console.error(err);
      setError(
        err.isForbidden
          ? 'Refunding a label is restricted to administrators.'
          : err.message || 'Failed to refund the shipment.'
      );
    } finally {
      setRefunding(null);
      setTimeout(() => setError(""), 6000);
    }
  };

  const handleDeleteOrder = async (orderNumber) => {
    setDeletingOrder(orderNumber);
    setError("");

    try {
      const orderShipments = shipments.filter(s => s.order_number === orderNumber);

      if (orderShipments.length === 0) {
        setError("No shipments found for this order");
        setDeletingOrder(null);
        return;
      }

      const results = [];
      const firstShipment = orderShipments[0];

      for (const shipment of orderShipments) {
        try {
          const shouldRefund =
            shipment.refund_status !== 'SUCCESS' && Boolean(shipment.transaction_id);
          const result = await api.shipping.remove(shipment.id, { refundFirst: shouldRefund });
          results.push({ id: shipment.id, ok: true, ...result });
        } catch (err) {
          console.error(`Failed to remove shipment ${shipment.id}:`, err);
          results.push({ id: shipment.id, ok: false, error: err.message });
        }
      }

      const failed = results.filter(r => !r.ok);
      const pending = results.filter(r => r.ok && r.soft);
      const stillLive = results.filter(r => r.ok && r.purged);

      if (failed.length > 0) {
        setError(`${failed.length} shipment(s) could not be removed: ${failed[0].error}`);
      } else if (stillLive.length > 0) {
        setError(
          `Removed ${results.length} shipment(s). ${stillLive.length} label(s) were never ` +
          `refunded and are still live — their Shippo transactions are in the activity log.`
        );
      } else if (pending.length > 0) {
        setError(
          `Removed ${results.length} shipment(s). ${pending.length} refund(s) are still ` +
          `processing, so those records are kept until they settle.`
        );
      }

      await logActivity({
        mailing_id: firstShipment.mailing_id,
        type: 'SHIPMENT_DELETED',
        description: `Removed all ${orderShipments.length} shipment(s) for order ${orderNumber}`
      });

      await fetchShipments();
      setDeleteOrderConfirm(null);

      if (selectedOrder && selectedOrder.order_number === orderNumber) {
        setSelectedOrder(null);
      }

    } catch (err) {
      console.error("Error deleting order:", err);
      setError(err.message || "Failed to delete order shipments");
    } finally {
      setDeletingOrder(null);
      setTimeout(() => setError(""), 5000);
    }
  };

  const handleUndeliver = async (mailingId) => {
    setUndelivering(mailingId);
    setError("");
    try {
      const mailingShipments = shipments.filter(s => s.mailing_id === mailingId);

      // Un-delivering is for hand deliveries and freight, which cost nothing to
      // undo. Anything with a purchased label has to go through the refund path.
      const withLabels = mailingShipments.filter(s => s.transaction_id && s.refund_status !== 'SUCCESS');
      if (withLabels.length > 0) {
        setError(
          `${withLabels.length} of these shipment(s) have live carrier labels. ` +
          `Refund them individually first — un-delivering would leave them paid for.`
        );
        setUndeliverConfirm(null);
        return;
      }

      await Promise.all(
        mailingShipments.map(s => api.shipping.remove(s.id, { refundFirst: false }))
      );

      await logActivity({
        mailing_id: mailingId,
        type: 'SHIPMENT_DELETED',
        description: 'Marked as un-delivered and moved back to mailings'
      });
      await fetchShipments();
      setSelectedOrder(null);
      setUndeliverConfirm(null);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to un-deliver");
      setTimeout(() => setError(""), 3000);
    } finally {
      setUndelivering(null);
    }
  };

  const handlePrintAllLabels = async (mailingId, orderNumber) => {
    setPrintingOrderLabels(mailingId);
    setError("");

    try {
      const result = await api.shipping.printAll(mailingId);

      if (result.skipped?.length > 0) {
        // Say which labels are missing rather than handing over a short PDF and
        // letting a box go out unlabelled.
        setError(
          `${result.skipped.length} label(s) could not be read and were left out: ` +
          `${result.skipped.join(', ')}. Reprint those individually.`
        );
        setTimeout(() => setError(""), 8000);
      }

      setPreviewPDF({
        url: result.url,
        title: `All labels — order ${orderNumber}`,
        type: 'merged_labels'
      });

      // Log activity
      const order = orders.find(o => o.mailing_id === mailingId);
      if (order) {
        await logActivity(
          mailingId,
          order.mailing_name,
          orderNumber,
          'PRINT_LABELS',
          `Printed all shipping labels (${order.total_boxes} labels)`,
          currentUser
        );
      }

    } catch (err) {
      console.error('Error merging labels:', err);
      setError(err.message || 'Failed to merge labels for printing. Please try again.');
      setTimeout(() => setError(""), 3000);
    } finally {
      setPrintingOrderLabels(null);
    }
  };

  const handleExportCSV = async () => {
    if (!selectedOrder || selectedOrder.shipments.length === 0) return;

    try {
      const mailingIds = [...new Set(selectedOrder.shipments.map(s => s.mailing_id).filter(Boolean))];
      const [mailingsResult, customPostOffices, shopSettings] = await Promise.all([
        Promise.all(mailingIds.map(id => api.entities.Mailing.filter({ id }))),
        api.entities.PostOffice.list(),
        api.settings.get()
      ]);

      /*
       * The handling fee, from Settings rather than typed into this file.
       *
       * DocketManager's import template carries one fee as a pair: a Type cell
       * holding '%' or '$', and an Amount cell holding the number. So the
       * percentage goes in Amount, with Type saying what it means. Its export
       * format is a different shape - separate Percent and Amount columns - and
       * matching the export rather than the template is what broke this import.
       *
       * The shop also bills a flat $1.25, kept in settings.handling_fee_amount,
       * and there is nowhere to put it: one fee, one type. DocketManager applies
       * its own configured flat fee on top.
       *
       * A fee that cannot be read stops the export: a billing file with a
       * guessed fee in it is worse than no file.
       */
      const handlingFeePercentValue = Number(shopSettings?.handling_fee_percent);
      if (!Number.isFinite(handlingFeePercentValue)) {
        throw new Error(
          'The default handling fee could not be read from Settings, so the export was stopped.'
        );
      }
      const handlingFeePercent = String(handlingFeePercentValue);


      /*
       * Shipment Date: today, with a time, as 11/2/2026 5:00:00 AM.
       *
       * Not the date the shipment was created. "If the Shipment Date is set to
       * a value in the past, import will fail", and a shipment's own date is
       * always in the past by the time anyone exports it.
       *
       * Built from the date and time separately because toLocaleString puts a
       * comma between them, and the same value goes on every row.
       */
      const now = new Date();
      const shipmentDate =
        `${now.toLocaleDateString('en-US')} ${now.toLocaleTimeString('en-US')}`;

      const mailingMap = {};
      mailingsResult.forEach(mailingArray => {
        if (mailingArray[0]) {
          mailingMap[mailingArray[0].id] = mailingArray[0];
        }
      });

      // Build a map of post office name -> address from custom post offices and mailing post offices
      const poAddressMap = {};
      customPostOffices.forEach(po => {
        poAddressMap[po.name] = {
          street1: po.address_line1 || '',
          city: po.city || '',
          state: po.state || '',
          zip: po.zip || ''
        };
      });
      // A mailing used to carry its destinations as an array on the record.
      // They are their own table now, so this supplemented the map from a
      // field that is never present - the post offices above are the source.

      const headers = [
        'Id',
        'Shipment Name',
        'Shipment Date',
        'Shipping Note',
        'Tracking Number',
        'Shipping Is Taxable',
        'Tax Rate',
        'Billing Use As Customer',
        'Billing Contact Name',
        'Billing Location Name',
        'Billing Location Address',
        'Billing Location City',
        'Billing Location Region',
        'Billing Location Country',
        'Billing Location Zip/Postal Code',
        'Destination Use As Customer',
        'Destination Contact Name',
        'Destination Location Name',
        'Destination Location Address',
        'Destination Location City',
        'Destination Location Region',
        'Destination Location Country',
        'Destination Location Zip/Postal Code',
        'Parcel Name',
        'Parcel Dimension Units',
        'Parcel Length',
        'Parcel Width',
        'Parcel Height',
        'Parcel Weight',
        'Parcel Mass Units',
        'Cost',
        'Default Handling Fee Type',
        'Default Handling Fee Amount',
        'Price',
        'OrderItem1',
        'Quantity1'
      ];

      const rows = selectedOrder.shipments.map(shipment => {
        const isFreight = shipment.shipment_type === "freight";
        const mailing = mailingMap[shipment.mailing_id];

        // The packed box behind this shipment. Freight rows carry no parcel,
        // so they have no box and no allocation.
        const box = isFreight
          ? null
          : mailing?.box_packing?.boxes?.find(
              b => b.container_number === shipment.box_number
            ) || null;

        // Use saved parcel dimensions/weight from shipment record first,
        // fall back to mailing box_packing for older records
        let parcelWeight = shipment.parcel_weight_lbs != null ? shipment.parcel_weight_lbs : null;
        let parcelLength = shipment.parcel_length != null ? shipment.parcel_length : null;
        let parcelWidth = shipment.parcel_width != null ? shipment.parcel_width : null;
        let parcelHeight = shipment.parcel_height != null ? shipment.parcel_height : null;

        if (!isFreight && (parcelWeight == null || parcelLength == null) && box) {
          if (parcelWeight == null) parcelWeight = box.weight || 10;
          if (parcelLength == null) parcelLength = box.container_size?.length || 12;
          if (parcelWidth == null) parcelWidth = box.container_size?.width || 12;
          if (parcelHeight == null) parcelHeight = box.container_size?.height || 12;
        }
        if (!isFreight) {
          if (parcelWeight == null) parcelWeight = 10;
          if (parcelLength == null) parcelLength = 12;
          if (parcelWidth == null) parcelWidth = 12;
          if (parcelHeight == null) parcelHeight = 12;
        }


        // Resolve post office address — saved address_to is most reliable,
        // then fall back to poAddressMap lookup by name
        const poName = shipment.post_office_name || '';
        const poAddr = shipment.address_to?.street1
          ? {
              street1: shipment.address_to.street1 || '',
              city: shipment.address_to.city || '',
              state: shipment.address_to.state || '',
              zip: shipment.address_to.zip || ''
            }
          : (poAddressMap[poName] || {
              street1: '',
              city: '',
              state: '',
              zip: ''
            });


        return [
          // Blank, and it has to stay blank. Id is DocketManager's own primary
          // key. A populated one means "update the shipment you already hold
          // under this id", so it looks the value up and fails the whole import
          // with "the given key was not present in the dictionary" when the id
          // is one we invented. The ids in DocketManager's own export are its
          // records, not ours - they were never a field to fill in.
          '',
          shipment.shipment_name || '',
          shipmentDate,
          isFreight ? `Freight - PRO: ${shipment.tracking_number || ''}` : '',
          shipment.tracking_number || '',
          'FALSE',
          '',
          'Address Location',
          shipment.post_office_name || '',
          shipment.post_office_name || '',
          poAddr.street1,
          poAddr.city,
          poAddr.state,
          'USA',
          poAddr.zip,
          'Address Location',
          shipment.post_office_name || '',
          shipment.post_office_name || '',
          poAddr.street1,
          poAddr.city,
          poAddr.state,
          'USA',
          poAddr.zip,
          isFreight ? 'Freight Shipment' : `Box #${shipment.box_number || ''}`,
          isFreight ? '' : 'Inches',
          isFreight ? '' : parcelLength.toString(),
          isFreight ? '' : parcelWidth.toString(),
          isFreight ? '' : parcelHeight.toString(),
          isFreight ? '' : Math.ceil(parcelWeight).toString(),
          isFreight ? '' : 'Pounds',
          shipment.total_cost || '0',
          '%',
          handlingFeePercent,
          // Price is left to the billing system, which derives it from the
          // three columns above.
          '',
          // OrderItem1 is the order number this job was given. Quantity1 is 0:
          // the row links the parcel to the order without moving any quantity,
          // which the order itself already accounts for.
          shipment.order_number || '',
          '0'
        ];
      });

      // One cell per header, on every row. A column added to one list and not
      // the other shifts every value after it - Cost landing under Price - and
      // a billing import reads that without complaint.
      const misaligned = rows.find(row => row.length !== headers.length);
      if (misaligned) {
        throw new Error(
          `The CSV columns are out of step (${headers.length} headers, ` +
          `${misaligned.length} cells), so the export was stopped.`
        );
      }

      /*
       * csvCell also guards against formula injection: a mailing name
       * beginning '=', '+', '-' or '@' was being written into the billing
       * system as a live formula. Rows are still joined with a bare newline
       * rather than through toCsv, which uses CRLF - these are the line
       * endings this import has always been handed.
       */
      const csvContent = [headers, ...rows]
        .map(row => row.map(csvCell).join(','))
        .join('\n');

      downloadFile(`shipments_${selectedOrder.order_number}.csv`, csvContent);

    } catch (err) {
      console.error('Error exporting CSV:', err);
      setError(err.message || 'Failed to export CSV. Please try again.');
      setTimeout(() => setError(""), 8000);
    }
  };

  const handleClosePDFViewer = () => {
    setPreviewPDF(null);
  };

  const filteredOrders = useMemo(() => orders.filter(order => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const orderNumber = order.order_number?.toLowerCase() || "";
    const mailingName = order.mailing_name?.toLowerCase() || "";
    return orderNumber.includes(search) || mailingName.includes(search);
  }), [orders, searchTerm]);

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Loading shipments...</div>
      </div>
    );
  }

  if (selectedOrder) {
    return (
      <div className="page-container">
        <header className="header">
          <div>
            <button
              onClick={() => setSelectedOrder(null)}
              style={{
                background: "transparent",
                border: "none",
                color: "#3b82f6",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: "0.875rem",
                marginBottom: "0.5rem",
                padding: 0
              }}
            >
              ← Back to Orders
            </button>
            <div className="header-title">{selectedOrder.mailing_name}</div>
            <div className="header-subtitle">Order {selectedOrder.order_number} - {selectedOrder.total_boxes} Shipments</div>
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {selectedOrder.shipments.every(s => s.carrier === "Hand Delivery") && (
              <button
                className="btn"
                onClick={() => setUndeliverConfirm(selectedOrder.mailing_id)}
                disabled={undelivering === selectedOrder.mailing_id}
                style={{ background: "#059669", color: "white", border: "none", display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <RefreshCcw size={20} />
                {undelivering === selectedOrder.mailing_id ? "Processing..." : "Un-Deliver All"}
              </button>
            )}
            <button
              className="btn btn-outline"
              onClick={handleExportCSV}
              disabled={selectedOrder.shipments.length === 0}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <Download size={20} />
              Export CSV
            </button>
            {selectedOrder.shipments.some(s => s.shipment_type !== "freight") && (
              <button
                className="btn btn-primary"
                onClick={() => handlePrintAllLabels(selectedOrder.mailing_id, selectedOrder.order_number)}
                disabled={printingOrderLabels === selectedOrder.mailing_id || selectedOrder.shipments.length === 0}
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <Printer size={20} />
                {printingOrderLabels === selectedOrder.mailing_id ? "Preparing..." : "Print All Labels"}
              </button>
            )}
          </div>
        </header>

        {error && <div className="error">{error}</div>}

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {selectedOrder.shipments.map((shipment) => (
            <div key={shipment.id} className="card" style={{ padding: "1.5rem" }}>
              {shipment.refund_id && (
                <div style={{
                  background: "#fef3c7",
                  border: "2px solid #fbbf24",
                  padding: "0.75rem 1rem",
                  borderRadius: "8px",
                  marginBottom: "1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <RefreshCcw size={16} style={{ color: "#92400e" }} />
                    <span style={{ fontSize: "0.875rem", fontWeight: 600, color: "#92400e" }}>
                      REFUNDED - {shipment.refund_status || 'Pending'}
                    </span>
                  </div>
                  <button
                    onClick={() => setReshipShipmentId(shipment.id)}
                    style={{
                      background: "#fbbf24",
                      color: "#92400e",
                      border: "none",
                      padding: "0.5rem 1rem",
                      borderRadius: "6px",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      marginLeft: "auto",
                      transition: "all 0.2s"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#f59e0b";
                      e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#fbbf24";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    Re-Ship
                  </button>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
                    <h3 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#1e293b" }}>
                      {shipment.shipment_name}
                    </h3>
                    {shipment.shipment_type === "freight" && (
                      <span style={{
                        background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa",
                        padding: "0.2rem 0.6rem", borderRadius: "12px", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.05em"
                      }}>FREIGHT</span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", color: "#64748b", fontSize: "0.875rem" }}>
                    {shipment.created_at && (
                      <span>
                        <Calendar size={14} style={{ display: "inline", marginRight: "0.25rem" }} />
                        {new Date(shipment.created_at).toLocaleDateString()}
                      </span>
                    )}
                    {shipment.box_number && shipment.shipment_type !== "freight" && (
                      <span>Box #{shipment.box_number}</span>
                    )}
                    {shipment.shipment_type === "freight" && shipment.pallet_box_count && (
                      <span>
                        {shipment.pallet_box_count} Box{shipment.pallet_box_count !== 1 ? "es" : ""} on Pallet
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  {shipment.carrier === "Hand Delivery" ? (
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => setUndeliverConfirm(selectedOrder.mailing_id)}
                      style={{ color: "#059669" }}
                      disabled={undelivering === selectedOrder.mailing_id}
                    >
                      <RefreshCcw size={16} />
                      Un-Deliver
                    </button>
                  ) : !shipment.refund_id && shipment.shipment_type !== "freight" && (
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => setRefundConfirm(shipment.id)}
                      style={{ color: "#f59e0b" }}
                      disabled={refunding === shipment.id}
                    >
                      <RefreshCcw size={16} />
                      {refunding === shipment.id ? "..." : "Refund"}
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => setDeleteConfirm(shipment.id)}
                    style={{ color: "#dc2626" }}
                    disabled={deletingShipment === shipment.id}
                  >
                    <Trash2 size={16} />
                    {deletingShipment === shipment.id ? "..." : "Delete"}
                  </button>
                </div>
              </div>

              <div style={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
                gap: "1rem",
                padding: "1rem",
                background: "#f8fafc",
                borderRadius: "8px"
              }}>
                {shipment.tracking_number && (
                  <div>
                    <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>
                      {shipment.shipment_type === "freight" ? "PRO Number" : "Tracking Number"}
                    </div>
                    {shipment.shipment_type === "freight" ? (
                      <div style={{ fontWeight: 600, color: "#c2410c" }}>{shipment.tracking_number}</div>
                    ) : (
                      <a
                        href={getTrackingUrl(shipment.carrier, shipment.tracking_number)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          color: "#3b82f6", 
                          fontWeight: 600, 
                          textDecoration: "none",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem"
                        }}
                      >
                        {shipment.tracking_number}
                        <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                )}
                
                {shipment.carrier && (
                  <div>
                    <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>
                      Carrier
                    </div>
                    <div style={{ fontWeight: 600 }}>
                      {shipment.carrier}
                      {shipment.service_level && ` (${shipment.service_level})`}
                    </div>
                  </div>
                )}

                {shipment.total_cost && (
                  <div>
                    <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>
                      Cost
                    </div>
                    <div style={{ 
                      fontWeight: 600, 
                      color: shipment.refund_id ? "#f59e0b" : "#10b981",
                      textDecoration: shipment.refund_id ? "line-through" : "none"
                    }}>
                      ${parseFloat(shipment.total_cost).toFixed(2)}
                    </div>
                  </div>
                )}

                {shipment.post_office_name && (
                  <div>
                    <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>
                      Destination
                    </div>
                    <div style={{ fontWeight: 600 }}>{shipment.post_office_name}</div>
                  </div>
                )}

                {shipment.routes_summary && (
                  <div>
                    <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>
                      Routes
                    </div>
                    <div style={{ fontWeight: 600 }}>{shipment.routes_summary}</div>
                  </div>
                )}
              </div>

              {shipment.label_path && shipment.shipment_type !== "freight" && (
                <div style={{ marginTop: "1rem" }}>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleViewLabel(shipment)}
                  >
                    <Package size={16} />
                    View 4×6 Shipping Label
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {undeliverConfirm && (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0, 0, 0, 0.5)", display: "flex",
            alignItems: "center", justifyContent: "center", zIndex: 1000
          }}>
            <div className="card" style={{ maxWidth: "400px", width: "90%" }}>
              <h3 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "1rem" }}>Confirm Un-Deliver</h3>
              <p style={{ color: "#64748b", marginBottom: "1.5rem" }}>
                This will remove all delivery records and move the mailing back to the "All Mailings" tab so it can be re-shipped.
              </p>
              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
                <button className="btn btn-outline" onClick={() => setUndeliverConfirm(null)}>Cancel</button>
                <button
                  className="btn"
                  style={{ background: "#059669", color: "white", border: "none" }}
                  onClick={() => handleUndeliver(undeliverConfirm)}
                  disabled={undelivering === undeliverConfirm}
                >
                  {undelivering === undeliverConfirm ? "Processing..." : "Un-Deliver"}
                </button>
              </div>
            </div>
          </div>
        )}

        {refundConfirm && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
          }}>
            <div className="card" style={{ maxWidth: "400px", width: "90%" }}>
              <h3 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "1rem" }}>
                Confirm Refund
              </h3>
              <p style={{ color: "#64748b", marginBottom: "1.5rem" }}>
                Are you sure you want to refund this shipping label? This action will void the label and request a refund from the carrier.
              </p>
              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
                <button
                  className="btn btn-outline"
                  onClick={() => setRefundConfirm(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  style={{ background: "#f59e0b" }}
                  onClick={() => handleRefund(refundConfirm)}
                  disabled={refunding === refundConfirm}
                >
                  {refunding === refundConfirm ? "Processing..." : "Refund Label"}
                </button>
              </div>
            </div>
          </div>
        )}

        {forceDelete && (
        <div className="modal-backdrop" onClick={() => setForceDelete(null)}>
          <div className="card modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>The label could not be refunded</h3>
                <p>{forceDelete.reason}</p>
              </div>
            </div>
            <p className="muted">
              Removing it now deletes the shipment permanently and leaves the
              label live, so the money stays spent. The Shippo transaction,
              tracking number and cost are written to the activity log first, so
              the refund can still be chased there — but not from this screen.
            </p>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setForceDelete(null)}>
                Keep it, and retry the refund
              </button>
              <button
                className="btn btn-primary"
                style={{ background: '#dc2626' }}
                onClick={() => {
                  const id = forceDelete.id;
                  setForceDelete(null);
                  handleDelete(id, true);
                }}
              >
                Remove anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
          }}>
            <div className="card" style={{ maxWidth: "400px", width: "90%" }}>
              <h3 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "1rem" }}>
                Confirm Delete
              </h3>
              <p style={{ color: "#64748b", marginBottom: "1.5rem" }}>
                Are you sure you want to delete this shipment? {!shipments.find(s => s.id === deleteConfirm)?.refund_id && 
                "The shipping label will be automatically refunded before deletion."}
              </p>
              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
                <button
                  className="btn btn-outline"
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deletingShipment === deleteConfirm}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  style={{ background: "#dc2626" }}
                  onClick={() => handleDelete(deleteConfirm)}
                  disabled={deletingShipment === deleteConfirm}
                >
                  {deletingShipment === deleteConfirm ? "Processing..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

        {previewPDF && (
          <PDFViewerModal
            pdfUrl={previewPDF.url}
            title={previewPDF.title}
            onClose={handleClosePDFViewer}
          />
        )}

        {reshipShipmentId && (
          <ReShipModal
            shipment={selectedOrder.shipments.find(s => s.id === reshipShipmentId)}
            mailing={selectedOrder}
            onClose={() => setReshipShipmentId(null)}
            onSuccess={() => {
              fetchShipments();
              setOrders(prevOrders => {
                const updated = prevOrders.find(o => o.mailing_id === selectedOrder.mailing_id);
                if (updated) setSelectedOrder(updated);
                return prevOrders;
              });
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="header">
        <div>
          <div className="header-title">Shipments</div>
          <div className="header-subtitle">Track your EDDM shipments by order</div>
        </div>
        <button
          className="btn"
          onClick={() => setShowActivityLogs(true)}
          style={{
            background: "#fef3c7",
            color: "#92400e",
            border: "2px solid #fbbf24"
          }}
        >
          <ClipboardList size={20} />
          Activity Logs
        </button>
      </header>

      {error && <div className="error">{error}</div>}

      <div className="form-section">
        <div className="form-group">
          <label className="form-label">
            <Search size={16} style={{ display: "inline", marginRight: "0.25rem" }} />
            Search Orders
          </label>
          <input
            type="text"
            className="form-input"
            placeholder="Search by order number or mailing name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "3rem" }}>
          <Truck size={64} style={{ margin: "0 auto 1rem", color: "#94a3b8" }} />
          <h3 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#475569", marginBottom: "0.5rem" }}>
            {searchTerm ? "No Matching Orders" : "No Shipments Yet"}
          </h3>
          <p style={{ color: "#64748b" }}>
            {searchTerm 
              ? "Try adjusting your search" 
              : "Shipments will appear here once you create shipping labels for your mailings"}
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {filteredOrders.map((order) => (
            <div 
              key={order.mailing_id}
              className="card" 
              style={{ 
                padding: "1.5rem",
                transition: "all 0.2s ease",
                borderLeft: order.has_refunds ? "4px solid #f59e0b" : "4px solid #3b82f6",
                position: "relative"
              }}
            >
              {/* Changed: Added Print All button and restructured delete button */}
              <div style={{
                position: "absolute",
                top: "1rem",
                right: "1rem",
                display: "flex",
                gap: "0.5rem",
                zIndex: 10
              }}>
                {order.shipments.some(s => s.shipment_type !== "freight") && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrintAllLabels(order.mailing_id, order.order_number);
                    }}
                    disabled={printingOrderLabels === order.mailing_id}
                    className="btn btn-sm btn-primary"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      fontSize: "0.75rem",
                      padding: "0.5rem 0.75rem"
                    }}
                  >
                    <Printer size={14} />
                    {printingOrderLabels === order.mailing_id ? "..." : "Print All"}
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteOrderConfirm(order.order_number);
                  }}
                  disabled={deletingOrder === order.order_number}
                  style={{
                    background: "transparent",
                    border: "2px solid #dc2626",
                    color: "#dc2626",
                    borderRadius: "8px",
                    padding: "0.5rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    transition: "all 0.2s"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#dc2626";
                    e.currentTarget.style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "#dc2626";
                  }}
                >
                  <Trash2 size={14} />
                  {deletingOrder === order.order_number ? "..." : "Delete"}
                </button>
              </div>

              <div 
                style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center",
                  cursor: "pointer",
                  paddingRight: "12rem" // Changed: Adjusted padding for new buttons
                }}
                onClick={() => setSelectedOrder(order)}
                onMouseEnter={(e) => {
                  const card = e.currentTarget.closest('.card');
                  card.style.transform = "translateY(-2px)";
                  card.style.boxShadow = "0 8px 16px rgba(0, 0, 0, 0.1)";
                }}
                onMouseLeave={(e) => {
                  const card = e.currentTarget.closest('.card');
                  card.style.transform = "translateY(0)";
                  card.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.06)";
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
                    <h3 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#1e293b" }}>
                      {order.mailing_name}
                    </h3>
                    {order.has_refunds && (
                      <span style={{
                        background: "#fef3c7",
                        color: "#92400e",
                        padding: "0.25rem 0.75rem",
                        borderRadius: "12px",
                        fontSize: "0.75rem",
                        fontWeight: 600
                      }}>
                        HAS REFUNDS
                      </span>
                    )}
                  </div>
                  
                  <div style={{ display: "flex", gap: "2rem", flexWrap: "wrap", color: "#64748b", fontSize: "0.875rem", marginBottom: "1rem" }}>
                    <span>Order: {order.order_number}</span>
                    <span>
                      <Calendar size={14} style={{ display: "inline", marginRight: "0.25rem" }} />
                      {new Date(order.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  <div style={{
                    display: "flex",
                    gap: "2rem",
                    padding: "1rem",
                    background: "#f8fafc",
                    borderRadius: "8px"
                  }}>
                    <div>
                      <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>
                        Total Boxes
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <Box size={16} style={{ color: "#3b82f6" }} />
                        <strong style={{ fontSize: "1.25rem", color: "#1e293b" }}>
                          {order.total_boxes}
                        </strong>
                      </div>
                    </div>
                    
                    <div>
                      <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "0.25rem" }}>
                        Total Cost
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <DollarSign size={16} style={{ color: order.has_refunds ? "#f59e0b" : "#10b981" }} />
                        <strong style={{ 
                          fontSize: "1.25rem", 
                          color: order.has_refunds ? "#f59e0b" : "#10b981"
                        }}>
                          ${order.total_cost.toFixed(2)}
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>

                <ChevronRight size={32} style={{ color: "#94a3b8" }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteOrderConfirm && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: "500px", width: "90%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
              <AlertTriangle size={24} style={{ color: "#dc2626" }} />
              <h3 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#dc2626" }}>
                Delete Entire Order
              </h3>
            </div>
            <p style={{ color: "#64748b", marginBottom: "1rem" }}>
              Are you sure you want to delete all shipments for order <strong>{deleteOrderConfirm}</strong>?
            </p>
            <div style={{ 
              background: "#fef2f2", 
              border: "2px solid #fecaca",
              padding: "1rem", 
              borderRadius: "8px", 
              marginBottom: "1.5rem" 
            }}>
              <p style={{ fontSize: "0.875rem", color: "#991b1b", fontWeight: 600, marginBottom: "0.5rem" }}>
                This will:
              </p>
              <ul style={{ fontSize: "0.875rem", color: "#991b1b", paddingLeft: "1.5rem", margin: 0 }}>
                <li>Automatically refund all shipping labels</li>
                <li>Delete all shipment records</li>
                <li>Restore the "Ship" button in Processed Mailings</li>
                <li>This action cannot be undone</li>
              </ul>
            </div>
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
              <button
                className="btn btn-outline"
                onClick={() => setDeleteOrderConfirm(null)}
                disabled={deletingOrder === deleteOrderConfirm}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ background: "#dc2626" }}
                onClick={() => handleDeleteOrder(deleteOrderConfirm)}
                disabled={deletingOrder === deleteOrderConfirm}
              >
                {deletingOrder === deleteOrderConfirm ? "Processing..." : "Delete & Refund All"}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewPDF && (
        <PDFViewerModal
          pdfUrl={previewPDF.url}
          title={previewPDF.title}
          onClose={handleClosePDFViewer}
        />
      )}

      {filteredOrders.length > 0 && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem", paddingBottom: "2rem" }}>
          <button
            className="btn btn-outline"
            onClick={() => setShipmentLimit(prev => prev + 500)}
            style={{ padding: "0.75rem 1.5rem", fontSize: "0.9rem" }}
          >
            Load More Shipments
          </button>
        </div>
      )}

      {showActivityLogs && (
        <ActivityLogsModal
          onClose={() => setShowActivityLogs(false)}
        />
      )}
    </div>
  );
}